import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { api, errorMessage, type Schemas, unwrap } from "./client";

export type RunSummary = Schemas["RunSummary"];
export type RunDetail = Schemas["RunDetail"];
export type Item = Schemas["ItemOut"];

export const RUNS_KEY = ["runs"] as const;
export const runKey = (id: string) => ["run", id] as const;

/** Cancel, retry-failed and delete, shared by the runs list and the run page. */
export function useRunActions() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const refresh = (id?: string) => {
    void queryClient.invalidateQueries({ queryKey: RUNS_KEY });
    if (id) void queryClient.invalidateQueries({ queryKey: runKey(id) });
  };
  const failed = (e: unknown) => toast.error(errorMessage(e));

  const cancel = useMutation({
    mutationFn: (id: string) => unwrap(api.POST("/api/runs/{run_id}/cancel", { params: { path: { run_id: id } } })),
    onSuccess: (_d, id) => {
      refresh(id);
      toast("Cancelling. Numbers already started will finish.");
    },
    onError: failed,
  });
  const pause = useMutation({
    mutationFn: (id: string) => unwrap(api.POST("/api/runs/{run_id}/pause", { params: { path: { run_id: id } } })),
    onSuccess: (_d, id) => {
      refresh(id);
      toast("Pausing. Numbers already started will finish.");
    },
    onError: failed,
  });
  const resume = useMutation({
    mutationFn: (id: string) => unwrap(api.POST("/api/runs/{run_id}/resume", { params: { path: { run_id: id } } })),
    onSuccess: (_d, id) => {
      refresh(id);
      toast.success("Run continued");
    },
    onError: failed,
  });
  const retry = useMutation({
    mutationFn: (id: string) =>
      unwrap(api.POST("/api/runs/{run_id}/retry-failed", { params: { path: { run_id: id } } })),
    onSuccess: (run) => {
      refresh(run.id);
      toast.success(`Round ${run.round} started for ${run.counts.pending} numbers`);
      navigate(`/runs/${run.id}`);
    },
    onError: failed,
  });
  const remove = useMutation({
    mutationFn: (id: string) => unwrap(api.DELETE("/api/runs/{run_id}", { params: { path: { run_id: id } } })),
    onSuccess: (_d, id) => {
      queryClient.removeQueries({ queryKey: runKey(id) });
      refresh();
      toast("Run deleted");
    },
    onError: failed,
  });
  return { cancel, pause, resume, retry, remove };
}

const AT_REST = ["completed", "cancelled", "halted", "paused"];

export function canCancel(run: RunSummary): boolean {
  return ["running", "pausing", "paused"].includes(run.state);
}

export function canPause(run: RunSummary): boolean {
  return run.state === "running";
}

// Stops a run cannot get past: its own provider account or number range is what fails.
const FINAL_HALTS = new Set(["provider_account_missing", "provider_operator_not_allowed"]);

/** A paused run, or a halted one once what stopped it is fixed. */
export function canResume(run: RunSummary): boolean {
  return run.state === "paused" || (run.state === "halted" && !FINAL_HALTS.has(run.halt_reason ?? ""));
}

/** Not for a run that buys numbers: it replaces its failures itself. */
export function canRetry(run: RunSummary): boolean {
  return run.source !== "provider" && run.retryable_failed > 0 && AT_REST.includes(run.state);
}

export function canDelete(run: RunSummary): boolean {
  return AT_REST.includes(run.state);
}
